import {
  GfFlyerReviewChangeRequest,
  GfFlyerReviewComment,
} from "@swagger/typescript-fetch-goflyer";
import { ReviewChangeRequest, toEpochMs } from "../../types";
import {
  COMMENT_TARGET,
  commentTargetOf,
  isOpenComment,
} from "../CommentsPanel/commentTypes";

const St = GfFlyerReviewChangeRequest.StatusEnum;

export interface ItemActivityGroup {
  flyerItemId: string;
  changeRequests: ReviewChangeRequest[];
  comments: GfFlyerReviewComment[];
  counts: {
    pending: number;
    accepted: number;
    rejected: number;
    superseded: number;
    restored: number;
    openComments: number;
  };
  latestActivityAt: number;
}

export interface WorkspaceStats {
  pendingCrTotal: number;
  itemCount: number;
  openFlyerCommentCount: number;
}

export interface ReviewActivity {
  groups: ItemActivityGroup[];
  flyerComments: GfFlyerReviewComment[];
  workspaceStats: WorkspaceStats;
}

const submittedOrCreatedMs = (cr: ReviewChangeRequest): number =>
  toEpochMs(cr.submittedAt || cr.createdAt);

const sortCrsDesc = (rows: ReviewChangeRequest[]): ReviewChangeRequest[] =>
  rows
    .slice()
    .sort((a, b) => submittedOrCreatedMs(b) - submittedOrCreatedMs(a));

const computeCounts = (
  changeRequests: ReviewChangeRequest[],
  comments: GfFlyerReviewComment[],
): ItemActivityGroup["counts"] => {
  let pending = 0;
  let accepted = 0;
  let rejected = 0;
  let superseded = 0;
  let restored = 0;
  for (const cr of changeRequests) {
    if (cr.status === St.PENDING) pending += 1;
    else if (cr.status === St.ACCEPTED) accepted += 1;
    else if (cr.status === St.REJECTED) rejected += 1;
    else if (cr.status === St.SUPERSEDED) superseded += 1;
    else if (cr.status === St.RESTORED) restored += 1;
  }
  let openComments = 0;
  for (const c of comments) {
    if (isOpenComment(c)) openComments += 1;
  }
  return { pending, accepted, rejected, superseded, restored, openComments };
};

/**
 * Pure projection of raw change requests + comments into the workspace
 * activity model: groups by flyer item, attaches item-level comments,
 * computes per-item counts, and sorts (most pending → most open comments →
 * most recent activity). Extracted from `useReviewActivity` so it is unit
 * testable and reusable as a react-query `select`.
 */
export const buildReviewActivity = (
  changeRequests: ReviewChangeRequest[],
  comments: GfFlyerReviewComment[],
): ReviewActivity => {
  let pendingCrTotal = 0;
  for (const cr of changeRequests) {
    if (cr.status === St.PENDING) pendingCrTotal += 1;
  }

  const flyerLevel: GfFlyerReviewComment[] = [];
  const itemComments = new Map<string, GfFlyerReviewComment[]>();
  for (const c of comments) {
    if (commentTargetOf(c) === COMMENT_TARGET.FLYER) {
      flyerLevel.push(c);
      continue;
    }
    const itemId = c.targetId;
    if (!itemId) continue;
    const bucket = itemComments.get(itemId) ?? [];
    bucket.push(c);
    itemComments.set(itemId, bucket);
  }

  const grouped = new Map<string, ItemActivityGroup>();
  const ensureGroup = (flyerItemId: string) => {
    let g = grouped.get(flyerItemId);
    if (!g) {
      g = {
        flyerItemId,
        changeRequests: [],
        comments: [],
        counts: {
          pending: 0,
          accepted: 0,
          rejected: 0,
          superseded: 0,
          restored: 0,
          openComments: 0,
        },
        latestActivityAt: 0,
      };
      grouped.set(flyerItemId, g);
    }
    return g;
  };

  for (const cr of changeRequests) {
    if (!cr.flyerItemId) continue;
    const g = ensureGroup(cr.flyerItemId);
    g.changeRequests.push(cr);
    g.latestActivityAt = Math.max(
      g.latestActivityAt,
      submittedOrCreatedMs(cr),
    );
  }
  itemComments.forEach((list, itemId) => {
    const g = ensureGroup(itemId);
    g.comments = list;
    for (const c of list) {
      g.latestActivityAt = Math.max(
        g.latestActivityAt,
        toEpochMs(c.createdAt),
      );
    }
  });

  const out: ItemActivityGroup[] = [];
  grouped.forEach((g) => {
    g.changeRequests = sortCrsDesc(g.changeRequests);
    g.counts = computeCounts(g.changeRequests, g.comments);
    out.push(g);
  });

  out.sort((a, b) => {
    if (a.counts.pending !== b.counts.pending) {
      return b.counts.pending - a.counts.pending;
    }
    if (a.counts.openComments !== b.counts.openComments) {
      return b.counts.openComments - a.counts.openComments;
    }
    return b.latestActivityAt - a.latestActivityAt;
  });

  let openFlyerCommentCount = 0;
  for (const c of flyerLevel) {
    if (isOpenComment(c)) openFlyerCommentCount += 1;
  }

  return {
    groups: out,
    flyerComments: flyerLevel,
    workspaceStats: {
      pendingCrTotal,
      itemCount: out.length,
      openFlyerCommentCount,
    },
  };
};
